/**
 * **悬空边的清理** —— 纯逻辑。
 *
 * ## 为什么会有悬空边
 *
 * 组 / 循环的外部端口是**推导**出来的：
 *
 *   组的输入  ← 外面连了几个（外面的变参行）
 *   组的输出  ← **里面**接了几路（`groupRowsFromGraph`）
 *   循环的输出 ← **里面**接了几路（`loopRowsFromGraph` → `wiredCount` 数内部边）
 *
 * 于是用户在**组 / 循环内部**删掉一条连线，外部的同名端口就可能消失，
 * 而挂在那个端口上的**外层边**没人管 —— 它指向一个不存在的 handle。
 *
 * 后果不是报错，而是**静默丢线**：Vue Flow 会拿 `isValidConnection` 复检
 * 每一条既有边，判非法就丢弃，并在控制台打出一句误导性的话：
 *
 *     [Vue Flow]: An edge needs a source and a target
 *
 * （见 README「坑 7 详解」：这条文案实际对应 `EDGE_INVALID`，
 *   并不是「edge 缺 source 字段」。）
 *
 * 与其让图引擎偷偷丢，不如我们丢得明白：刷新接口之后主动摘掉，
 * 并把摘掉几条告诉用户。
 */

export interface EdgeLike {
  id?: string
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

/** 端口解析器：解析不出来返回 undefined */
export type SocketResolver = (
  nodeId: string,
  handleId: string | null | undefined,
) => unknown

export interface PruneResult<E> {
  kept: E[]
  removed: E[]
}

/**
 * 摘掉两端有任意一端解析不出端口的边。
 *
 * 注意：**两端都要查**。只查 target 会漏掉「源节点的输出口被删掉」的情况
 * （循环的输出口正是源侧 —— 我们踩的就是这个）。
 */
export function pruneDanglingEdges<E extends EdgeLike>(
  edges: readonly E[],
  resolve: SocketResolver,
): PruneResult<E> {
  const kept: E[] = []
  const removed: E[] = []
  for (const edge of edges) {
    const from = resolve(edge.source, edge.sourceHandle)
    const to = resolve(edge.target, edge.targetHandle)
    if (from && to) kept.push(edge)
    else removed.push(edge)
  }
  return { kept, removed }
}

/** 给提示条用的一句话 */
export function pruneNotice(removed: number): string | undefined {
  if (removed <= 0) return undefined
  return removed === 1
    ? '已移除 1 条指向不存在端口的连线'
    : `已移除 ${removed} 条指向不存在端口的连线`
}
